/**
 * Sign-in code input helpers shared by the login page. The editor has its
 * own copy in editor/webapp/js/sign-in-code.ts; keep the two in step.
 */

export const LOGIN_CODE_LENGTH = 6;

const STANDALONE_CODE = new RegExp(
  `(?<!\\d)\\d{${LOGIN_CODE_LENGTH}}(?!\\d)`,
);

/**
 * The code in pasted text: the digits alone ("123456", "123 456",
 * "123-456"), or the one six-digit number in a longer text such as an
 * email line. Null when there is none.
 */
export function extractLoginCode(text) {
  const value = String(text ?? "").trim();
  const compact = value.replace(/[\s-]/g, "");
  if (new RegExp(`^\\d{${LOGIN_CODE_LENGTH}}$`).test(compact)) {
    return compact;
  }
  return value.match(STANDALONE_CODE)?.[0] ?? null;
}

/** What the code field may hold while typing: digits only, at most six. */
export function sanitizeLoginCodeInput(value) {
  return String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, LOGIN_CODE_LENGTH);
}

/** The message for a failed verify-code answer. */
export function describeLoginCodeError(data, status) {
  switch (data?.code) {
    case "invalid_code": {
      const left = Number(data.attemptsRemaining);
      return Number.isFinite(left)
        ? `That code isn't right. ${left} ${left === 1 ? "try" : "tries"} left.`
        : "That code isn't right.";
    }
    case "code_expired":
      return "This code has expired. Send a new one.";
    case "too_many_attempts":
      return "Too many wrong codes. Wait a while, then send a new code.";
    case "resend_too_soon":
    case "too_many_requests":
    case "invalid_email":
    case "invalid_code_format":
    case "mail_failed":
      return data.error;
    default:
      return status >= 500
        ? "Something went wrong. Try again."
        : data?.error || "Sign-in failed. Try again.";
  }
}
